'use client'

import type { BasketError } from '@/app/[locale]/[ferry]/_actions/checkBasketValidity'

import React from 'react'
import { useTranslation } from 'react-i18next'

import { useParams } from 'next/navigation'

import { ButtonClick, track } from '@dfds-frontend/tracking'
import { getClientEnvironment } from '@dfds-pax/common'
import { HELLMAN_ORIGIN } from '@dfds-pax/utils'

import Dialog from '../../Dialog'
import BackendErrorsNonProd from './BackendErrorsNonProd'

interface Props {
  basketError?: BasketError | null
}

const BackendErrorModal: React.FC<Props> = ({ basketError }) => {
  const params = useParams()
  const { locale } = params as { locale: string }
  const { t } = useTranslation()

  if (!basketError) return null

  let show = false
  let heading = t('ERROR-OCCURED-HEADING')
  let description = t('ERROR-OCCURED-DESCRIPTION')
  let buttonText = t('ERROR-START-OVER')
  let dataAttr = `BACKEND-ERROR-CODE-${basketError.type}`

  if (basketError.type === 'PRICECHANGED') {
    heading = t('PRICE-HAS-CHANGED-HEADING')
    description = t('PRICE-HAS-CHANGED-DESCRIPTION')
    buttonText = t('REFRESH-DEPARTURES-BUTTON')
    show = true
  } else if (basketError.type === 'NOCAPACITY') {
    heading = t('NO-CAPACITY-HEADING')
    description = t('NO-CAPACITY-DESCRIPTION')
    buttonText = t('REFRESH-DEPARTURES-BUTTON')
    show = true
  } else if (basketError.type === 'DELETED') {
    heading = t('ERROR-RESERVATION-ROLLED-BACK-TITLE')
    description = t('ERROR-RESERVATION-ROLLED-BACK-DESCRIPTION')
    buttonText = t('ERROR-START-OVER')
    show = true
    dataAttr = 'reservation-deleted'
  } else if (basketError.type === 'SALESCLOSED') {
    heading = t('SALES-CLOSED-HEADER')
    description = t('SALES-CLOSED-DESCRIPTION')
    buttonText = t('REFRESH-DEPARTURES-BUTTON')
    show = true
    dataAttr = 'sales-closed'
  } else if (basketError.type === 'UNKNOWNERROR') {
    show = true
  }

  const restartBooking = async () => {
    if (basketError.type === 'UNKNOWNERROR') trackClick('restartBooking', 'reservationErrorStartOver')
    else if (basketError.type === 'NOCAPACITY') trackClick('restartBooking', 'noCapacity')
    else if (basketError.type === 'PRICECHANGED') trackClick('restartBooking', 'priceHasChanged')
    else if (basketError.type === 'DELETED') trackClick('restartBooking', 'reservationRollback')
    else if (basketError.type === 'SALESCLOSED') trackClick('restartBooking', 'salesClosed')

    if (['NOCAPACITY', 'PRICECHANGED', 'SALESCLOSED'].includes(basketError.type)) {
      sessionStorage.setItem('bypassCache', 'true')
    }

    const restartBookingUrl = sessionStorage.getItem('restartBookingUrl') || location.pathname.split('/').slice(0, -1).join('/')
    window.location.replace(restartBookingUrl)
  }

  const recoverBasket = () => {
    if (basketError.type === 'UNKNOWNERROR') trackClick('recoverBasket', 'reservationErrorStartOver')
    else if (basketError.type === 'DELETED') trackClick('recoverBasket', 'reservationRollback')
    const environment = getClientEnvironment()
    const recoverBasketLink = `${HELLMAN_ORIGIN}/${environment}/booking-experience-bff/_api/passenger-ferries/booking-experience/baskets/${basketError.basketId}/${locale}/recoverBasket/`
    window.location.replace(recoverBasketLink)
  }

  return (
    <>
      <Dialog
        dataAttr={dataAttr}
        description={description}
        headline={heading}
        onPrimaryButtonClicked={restartBooking}
        onSecondaryButtonClicked={['DELETED', 'UNKNOWNERROR'].includes(basketError.type) ? recoverBasket : undefined}
        primaryButtonText={buttonText}
        secondaryButtonText={['DELETED', 'UNKNOWNERROR'].includes(basketError.type) ? t('RECOVER-BASKET-BTN') : undefined}
        show={show}
      />
      <BackendErrorsNonProd basketError={basketError} />
    </>
  )
}

export default BackendErrorModal

const trackClick = (eventAction: string, clickedElementText: string): void => {
  track<ButtonClick>({
    clickedElementText,
    event: 'buttonClick',
    eventAction,
  })
}
